import * as React from 'react';
import composeRefs from '@seznam/compose-react-refs';
import styled, { css } from 'styled-components';
import getColour from '@lendi-ui/commons/colours';
import getTypography from '@lendi-ui/commons/typography';
import { getRadius } from '@lendi-ui/commons/borders';
import { normalise, deriveSize, NORMALISE_LINE_HEIGHT } from '@lendi-ui/utils';
import { map } from '@lendi-ui/breakpoint';
import { jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var styleBySizeMixin = (size, rows) => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["padding:", " ", ";font-size:", ";height:calc(", " * ", " * ", " + 2 * ", ");"], deriveSize(0.5), deriveSize(0.75), deriveSize(0.875), NORMALISE_LINE_HEIGHT, rows, deriveSize(0.875), deriveSize(0.5));

    case 'sm':
      return css(["padding:", " ", ";font-size:", ";height:calc(", " * ", " * ", " + 2 * ", ");"], deriveSize(0.5), deriveSize(0.75), deriveSize(1), NORMALISE_LINE_HEIGHT, rows, deriveSize(1), deriveSize(0.5));

    case 'md':
      return css(["padding:", " ", ";font-size:", ";height:calc(", " * ", " * ", " + 2 * ", ");"], deriveSize(0.75), deriveSize(1), deriveSize(1.125), NORMALISE_LINE_HEIGHT, rows, deriveSize(1.125), deriveSize(0.75));

    case 'lg':
      return css(["padding:", " ", ";font-size:", ";height:calc(", " * ", " * ", " + 2 * ", ");"], deriveSize(1), deriveSize(1.5), deriveSize(1.375), NORMALISE_LINE_HEIGHT, rows, deriveSize(1.375), deriveSize(1));

    default:
      return css(["padding:", " ", ";font-size:", ";height:calc(", " * ", " * ", " + 2 * ", ");"], deriveSize(0.75), deriveSize(1), deriveSize(1.125), NORMALISE_LINE_HEIGHT, rows, deriveSize(1.125), deriveSize(0.75));
  }
});

var InputBorderColor = _ref => {
  var {
    isInverse,
    isError
  } = _ref;
  if (isError) return getColour('interaction', 'error');
  if (isInverse) return getColour('shade', 25);
  return getColour('border', 'interaction');
};

var InputBorderHoverColor = _ref2 => {
  var {
    isInverse,
    isError
  } = _ref2;
  if (isError) return getColour('interaction', 'error');
  if (isInverse) return getColour('shade', 0); // this used to be shade.400 even though colour handover says Now doesn't have a border hover colour

  return getColour('border', 'interaction', {
    state: 'hover'
  });
};

var TextAreaWrapper = styled.textarea.withConfig({
  displayName: "indexstyle__TextAreaWrapper",
  componentId: "lui__sc-o0l6w5-0"
})(["", ";resize:none;overflow:auto;border-radius:", ";box-sizing:border-box;font-family:", ";", ";", ";::placeholder{color:", ";}:hover:not(:focus){", "}"], normalise, getRadius(), getTypography('body1', 'fontFamily'), _ref3 => {
  var {
    size,
    isInverse,
    isFullWidth,
    rows
  } = _ref3;
  return css(["color:", ";", ";", ";"], isInverse ? getColour('shade', 0) : getColour('text', 'main'), isFullWidth ? css(["width:100%;"]) : css(["width:auto;"]), styleBySizeMixin(size, rows));
}, _ref4 => {
  var {
    isInverse,
    isDisabled
  } = _ref4;

  if (isDisabled) {
    return css(["cursor:not-allowed;background-color:", ";border:1px solid ", ";"], getColour('fill', 'disabled'), isInverse ? getColour('shade', 0) : getColour('border', 'interaction'));
  } else {
    return css(["border:1px solid ", ";background-color:", ";"], InputBorderColor, isInverse ? 'transparent' : getColour('fill', 'main'));
  }
}, getColour('text', 'subtle'), _ref5 => {
  var {
    isDisabled
  } = _ref5;
  return isDisabled ? undefined : css(["border:1px solid ", ";"], InputBorderHoverColor);
});

var _excluded = ["size", "isError", "isInverse", "isFullWidth", "isDisabled", "maxRows", "onChange", "rows", "className"];
var TextArea = /*#__PURE__*/React.forwardRef((_ref, parentRef) => {
  var {
    size = 'md',
    isError = false,
    isInverse = false,
    isFullWidth = false,
    isDisabled = false,
    maxRows,
    onChange,
    rows: initialRows = 3,
    className = ''
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var [rows, setRows] = React.useState(maxRows && initialRows > maxRows ? maxRows : initialRows); // we can use this to tell us what the new row value needs to be

  var [scrollHeightRatio, setScrollHeightRatio] = React.useState(0);
  var [scrollHeight, setScrollHeight] = React.useState(0); // remove padding from scrollheight calculation

  var getScrollHeightMinusPadding = node => {
    if (typeof window !== 'undefined') {
      var paddingY = parseFloat(window.getComputedStyle(node, null).getPropertyValue('padding-top')) * 2; // scrollHeight minus vertical padding is the value of (rows * lineHeight)

      return node.scrollHeight - paddingY;
    }

    return node.scrollHeight;
  };

  var scrollHeightRef = React.useCallback(node => {
    if (node !== null) {
      var currentScrollHeight = getScrollHeightMinusPadding(node); // get ratio of each line

      setScrollHeightRatio(currentScrollHeight / rows); // get scrollheight

      setScrollHeight(currentScrollHeight);
    }

    return node;
  }, []); // if rows if greater than maxRows by mistake then set rows to the maxRows

  var setRowsWithMax = rows => {
    if (maxRows && rows > maxRows) {
      setRows(maxRows);
    } else {
      setRows(rows);
    }
  };

  var handleChange = event => {
    if (maxRows) {
      // set rows to initial rows every time and allow us to change height
      event.target.rows = initialRows;
      event.target.style.height = 'auto'; // get the current scrollHeight without vertical paddings

      var newScrollHeight = getScrollHeightMinusPadding(event.target); // when the incoming change affect scrollHeight then we change the scrollHeight and rows

      if (scrollHeight !== newScrollHeight) {
        setScrollHeight(newScrollHeight);
        setRowsWithMax(Math.round(newScrollHeight / scrollHeightRatio));
      } // once the incoming change rows is greater than maxRows then we make it scrollable


      if (Math.round(newScrollHeight / scrollHeightRatio) > maxRows) {
        event.target.scrollTop = event.target.scrollHeight;
      } // finally change rows and height back


      event.target.rows = rows;
      event.target.style.height = '';
    }

    if (onChange) {
      onChange(event);
    }
  };

  return /*#__PURE__*/jsx(TextAreaWrapper, _objectSpread2({
    size: size,
    "data-testid": "lui-textarea",
    id: "lui-textarea",
    rows: rows,
    ref: composeRefs(parentRef, scrollHeightRef),
    isError: isError,
    isInverse: isInverse,
    isFullWidth: isFullWidth,
    disabled: isDisabled,
    isDisabled: isDisabled,
    className: className,
    onChange: handleChange
  }, otherProps));
});

export { TextArea, TextArea as default };
